import { AlertTriangle, CheckCircle2, Lightbulb, Link2, Users } from "lucide-react";
import { LinkSlipSelect } from "@/components/SlipNameLinks";

const _norm = (s) => String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

const DiffRow = ({ d, i, slipNames, onLink }) => (
  <tr className="border-t" data-testid={`slip-diff-row-${i}`}>
    <td className="p-1.5">{d.customer}</td>
    <td className="p-1.5">
      <div className="font-medium">{d.product}</div>
      <div className="flex flex-wrap gap-1 text-[10px] mt-0.5">
        {d.slip_product && _norm(d.slip_product) !== _norm(d.product) && (
          <span className="rounded bg-sky-50 text-sky-700 border border-sky-200 px-1.5">
            <Link2 className="inline w-3 h-3 mr-0.5 -mt-0.5" />Slip: {d.slip_product}
          </span>)}
        {!d.slip_product && (
          <span className="rounded bg-rose-50 text-rose-700 border border-rose-200 px-1.5">Not found on slip</span>)}
        {d.pooled && (
          <span className="rounded bg-amber-50 text-amber-800 border border-amber-200 px-1.5"
                title="One slip line shared by several customers — this row's slip qty is its share">
            <Users className="inline w-3 h-3 mr-0.5 -mt-0.5" />Shared slip total {d.pool_total} · {d.pool_customers} customers
          </span>)}
      </div>
      {!d.slip_product && slipNames.length > 0 && (
        <LinkSlipSelect d={d} i={i} slipNames={slipNames} onLink={onLink} />)}
    </td>
    <td className="p-1.5 text-center">{d.output_qty}</td>
    <td className="p-1.5 text-center">{d.source_qty}</td>
    <td className={`p-1.5 text-center font-bold ${d.delta > 0 ? "text-rose-600" : "text-amber-600"}`}>
      {d.delta > 0 ? `+${d.delta}` : d.delta}
    </td>
  </tr>
);

export const SlipCheckResult = ({ res, onLink, onAttach, onUnlink, invoiceNo }) => {
  if (!res) return null;
  return (
    <div className="space-y-3" data-testid="slip-check-result">
      {res.ok ? (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-emerald-800 text-sm" data-testid="slip-check-ok">
          <CheckCircle2 className="w-4 h-4" /> Slip matches the invoice — {res.parcels} parcel(s), {res.slip_units} units. Generate the Packing List.
        </div>
      ) : (
        <div className="flex items-center gap-2 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-rose-800 text-sm" data-testid="slip-check-bad">
          <AlertTriangle className="w-4 h-4" />
          {res.diffs.length + res.extra_on_slip.length + (res.invoice_match ? 0 : 1)} issue(s) — fix and upload the corrected slip
        </div>
      )}
      {res.suggestions.length > 0 && (
        <ul className="rounded-lg border border-amber-200 bg-amber-50/60 p-2 text-xs text-amber-900 space-y-1" data-testid="slip-check-suggestions">
          {res.suggestions.map((t, i) => (
            <li key={i} className="flex gap-1.5"><Lightbulb className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-600" />{t}</li>
          ))}
        </ul>
      )}
      {(res.stale_links || []).length > 0 && (
        <div className="rounded border border-orange-300 bg-orange-50 p-2 text-xs space-y-1" data-testid="slip-stale-links">
          <p className="font-semibold text-orange-800">Suspicious name links</p>
          {res.stale_links.map(a => (
            <div key={a.id} className="flex items-center justify-between gap-2">
              <span><b>{a.file_name}</b> is linked to <b>{a.slip_name}</b>, but the slip has <b>{a.file_name}</b> itself</span>
              {onUnlink && <button type="button" onClick={() => onUnlink(a)} data-testid={`slip-stale-unlink-${a.id}`}
                className="shrink-0 rounded bg-orange-600 text-white px-2 py-0.5 font-semibold hover:bg-orange-700">Remove link</button>}
            </div>))}
        </div>)}
      {res.diffs.length > 0 && (
        <div className="max-h-[38vh] overflow-auto rounded border">
          <table className="w-full text-xs" data-testid="slip-check-diffs">
            <thead className="bg-slate-100 sticky top-0">
              <tr><th className="p-1.5 text-left">Customer</th><th className="p-1.5 text-left">Product</th>
                <th className="p-1.5">Register Qty</th><th className="p-1.5">Slip Qty</th><th className="p-1.5">Diff</th></tr>
            </thead>
            <tbody>
              {res.diffs.map((d, i) => <DiffRow key={i} d={d} i={i} slipNames={res.slip_products || []} onLink={onLink} />)}
            </tbody>
          </table>
        </div>
      )}
      {res.extra_on_slip.length > 0 && (
        <div className="rounded border border-violet-200 bg-violet-50/50 p-2 text-xs" data-testid="slip-check-extra">
          <p className="font-semibold text-violet-800 mb-1">On the slip but not in this invoice</p>
          {res.extra_on_slip.map((e, i) => {
            const rr = e.register_rows || [];
            const blank = rr.filter(x => !x.invoice_no);
            return (
              <div key={i} className="py-1 border-b last:border-0 border-violet-100" data-testid={`slip-extra-${i}`}>
                <div className="flex justify-between"><span className="font-medium">{e.product}</span><span className="font-mono">{e.qty}</span></div>
                {rr.slice(0, 4).map(x => (
                  <div key={x.id} className="text-[10px] text-slate-600 pl-2">
                    In register: {x.customer_name} · {x.code} · {x.date} · {x.invoice_no ? <b className="text-rose-600">invoice {x.invoice_no}</b> : <b className="text-amber-700">no invoice #</b>}
                  </div>))}
                {blank.length > 0 && onAttach && (
                  <button type="button" onClick={() => onAttach(blank.map(x => x.id))}
                          className="mt-1 text-[11px] font-semibold rounded bg-violet-600 text-white px-2 py-0.5 hover:bg-violet-700"
                          data-testid={`slip-extra-attach-${i}`}>Attach {blank.length} row(s) to {invoiceNo}</button>)}
                {!rr.length && <div className="text-[10px] text-slate-500 pl-2">Not found in the register for this invoice's clients.</div>}
              </div>);
          })}
        </div>
      )}
    </div>
  );
};
